import { Box, Flex, Text, Heading, Button } from "@chakra-ui/react"
import { Star, ArrowRight, Map } from "lucide-react"

interface GameJudgeProps {
  visible: boolean
  stars: number // 1-3
  onNext: () => void
  onBackToMap: () => void
  isLastLevel: boolean
}

/* ── Confetti piece (pure CSS animation) ── */
function ConfettiPiece({ index }: { index: number }) {
  const colors = [
    "emerald.400",
    "amber.400",
    "blue.400",
    "pink.400",
    "purple.400",
    "orange.400",
    "teal.400",
    "red.400",
  ]
  const color = colors[index % colors.length]
  const left = `${10 + ((index * 17) % 80)}%`
  const delay = `${(index * 0.3) % 2}s`
  const duration = `${2 + (index % 3)}s`

  return (
    <Box
      position="absolute"
      top="-10px"
      left={left}
      w="3"
      h="3"
      bg={color}
      borderRadius={index % 2 === 0 ? "sm" : "full"}
      css={{
        animation: `confettiFall ${duration} ease-in ${delay} infinite`,
        "@keyframes confettiFall": {
          "0%": {
            transform: "translateY(0) rotate(0deg)",
            opacity: 1,
          },
          "100%": {
            transform: `translateY(400px) rotate(${720 + index * 45}deg)`,
            opacity: 0,
          },
        },
      }}
    />
  )
}

export function GameJudge({
  visible,
  stars,
  onNext,
  onBackToMap,
  isLastLevel,
}: GameJudgeProps) {
  if (!visible) return null

  return (
    <Box
      position="fixed"
      inset="0"
      zIndex="50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="blackAlpha.600"
      css={{
        animation: "fadeIn 0.3s ease-out",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {/* Confetti */}
      <Box position="absolute" inset="0" overflow="hidden" pointerEvents="none">
        {Array.from({ length: 12 }, (_, i) => (
          <ConfettiPiece key={i} index={i} />
        ))}
      </Box>

      {/* Success card */}
      <Box
        bg="white"
        borderRadius="2xl"
        p="8"
        maxW="sm"
        w="full"
        mx="4"
        textAlign="center"
        shadow="2xl"
        position="relative"
        css={{
          animation: "scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          "@keyframes scaleIn": {
            from: { transform: "scale(0.8)", opacity: 0 },
            to: { transform: "scale(1)", opacity: 1 },
          },
        }}
      >
        {/* Trophy emoji */}
        <Text fontSize="5xl" mb="2">
          🎉
        </Text>

        <Heading as="h2" size="xl" color="slate.900" mb="3">
          闯关成功！
        </Heading>

        {/* Stars */}
        <Flex justify="center" gap="2" mb="6">
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              color={i <= stars ? "amber.400" : "gray.200"}
              css={
                i <= stars
                  ? {
                      animation: `starPop 0.3s ease-out ${0.5 + i * 0.15}s both`,
                      "@keyframes starPop": {
                        from: { transform: "scale(0)", opacity: 0 },
                        to: { transform: "scale(1)", opacity: 1 },
                      },
                    }
                  : undefined
              }
            >
              <Star size={36} fill={i <= stars ? "currentColor" : "none"} />
            </Box>
          ))}
        </Flex>

        {/* Buttons */}
        <Flex direction="column" gap="3">
          <Button
            onClick={onNext}
            bg="emerald.500"
            color="white"
            size="lg"
            borderRadius="xl"
            _hover={{ bg: "emerald.600" }}
            fontWeight="semibold"
          >
            <Flex align="center" gap="2">
              {isLastLevel ? "完成课程" : "下一关"}
              <ArrowRight size={18} />
            </Flex>
          </Button>

          <Button
            onClick={onBackToMap}
            variant="ghost"
            color="gray.500"
            size="md"
            _hover={{ color: "gray.700", bg: "gray.50" }}
          >
            <Flex align="center" gap="2">
              <Map size={16} />
              返回关卡地图
            </Flex>
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}
